'use client'

import Link from 'next/link'
import VideoThumbnail from '@/components/VideoThumbnailFromApi'

interface VideoCardNewProps {
  listingId: string
  title: string
  thumbnailUrl?: string
  videoId?: number
  duration?: string
  category?: string
  creatorName?: string
  progress?: number // 0-100 watch progress percentage
  playlistId?: string
  playlistIndex?: number
}

/**
 * Netflix-style video card with hover overlay, progress bar, and category badge.
 * Used in carousels and grid layouts throughout the app.
 */
export default function VideoCardNew({
  listingId,
  title,
  thumbnailUrl,
  videoId,
  duration,
  category,
  creatorName,
  progress = 0,
  playlistId,
  playlistIndex,
}: VideoCardNewProps) {
  const href = buildHref(listingId, playlistId, playlistIndex)

  return (
    <Link
      href={href}
      className="group flex-shrink-0 w-[280px] snap-start cursor-pointer block"
      aria-label={`Play ${title}`}
    >
      {/* Card Inner */}
      <div
        className={
          'relative w-full aspect-video bg-muted rounded overflow-hidden ' +
          'transition-[transform,box-shadow] duration-[250ms] ease-[ease] ' +
          'group-hover:scale-[1.08] group-hover:shadow-[0_0_0_2px_#D4A017,0_0_32px_rgba(212,160,23,0.45)] group-hover:z-10'
        }
      >
        {/* Thumbnail */}
        {(videoId != null || thumbnailUrl) ? (
          <VideoThumbnail
            videoId={videoId ?? null}
            thumbnailUrl={thumbnailUrl}
            alt={title}
            className="w-full h-full object-cover"
          />
        ) : (
          <TitleFallback title={title} />
        )}

        {/* Category Badge */}
        {category && <CategoryBadge category={category} />}

        {/* Hover Overlay */}
        <div
          className={
            'absolute inset-0 flex flex-col justify-end p-[14px] ' +
            'bg-gradient-to-t from-[rgba(5,5,5,0.98)] via-[rgba(5,5,5,0.5)] to-transparent ' +
            'opacity-0 transition-opacity duration-[250ms] ease-[ease] group-hover:opacity-100'
          }
        >
          {/* Play Button */}
          <div
            className="mb-[10px] flex h-9 w-9 items-center justify-center rounded-full bg-gold"
            aria-hidden="true"
          >
            <svg
              width="14"
              height="14"
              fill="currentColor"
              viewBox="0 0 24 24"
              className="ml-[2px] text-[#050505]"
            >
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </div>

          {/* Info Row */}
          <div className="mb-1 flex items-center gap-2">
            {duration && (
              <span className="font-condensed text-[11px] font-semibold tracking-[1px] text-gold">
                {duration}
              </span>
            )}
            {duration && creatorName && (
              <span
                className="h-[3px] w-[3px] rounded-full bg-white/30"
                aria-hidden="true"
              />
            )}
            {creatorName && (
              <span className="font-condensed text-[11px] tracking-[0.5px] text-muted-foreground">
                {creatorName}
              </span>
            )}
          </div>

          {/* Title */}
          <h3 className="font-condensed text-[14px] font-bold leading-[1.2] tracking-[0.5px] text-white line-clamp-2">
            {title}
          </h3>
        </div>

        {/* Progress Bar */}
        {progress > 0 && (
          <div
            className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/15"
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${progress}% watched`}
          >
            <div
              className="h-full bg-gold"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        )}
      </div>
    </Link>
  )
}

/** Builds the video page href, appending playlist query params when provided. */
function buildHref(
  listingId: string,
  playlistId?: string,
  playlistIndex?: number,
): string {
  const base = `/video/${listingId}`
  if (!playlistId) return base

  const params = new URLSearchParams({ playlist: playlistId })
  if (playlistIndex !== undefined) {
    params.set('index', String(playlistIndex))
  }
  return `${base}?${params.toString()}`
}

/** Gradient background with title text when no thumbnail is available. */
function TitleFallback({ title }: { title: string }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#1a1a1a] to-[#050505] p-4">
      <span className="font-condensed text-sm font-semibold tracking-wide text-muted-foreground text-center line-clamp-3">
        {title}
      </span>
    </div>
  )
}

/** Skewed category badge pinned to the top-right of the card. */
function CategoryBadge({ category }: { category: string }) {
  return (
    <span className="clip-skew-xs absolute top-[10px] right-[10px] bg-[rgba(5,5,5,0.8)] border border-border px-2 py-[3px] font-condensed text-[9px] font-bold uppercase tracking-[1.5px] text-gold">
      {category}
    </span>
  )
}
